import classNames from "classnames";
import isEmpty from "lodash/fp/isEmpty";
import { useRef, useState } from "react";

import Button from "@rio-cloud/rio-uikit/Button";
import ButtonDropdown from "@rio-cloud/rio-uikit/ButtonDropdown";
import NotFoundState from "@rio-cloud/rio-uikit/NotFoundState";
import SortArrows from "@rio-cloud/rio-uikit/SortArrows";
import SortDirection, { type SortDirectionType, } from "@rio-cloud/rio-uikit/SortDirection";
import TableCardsSorting from "@rio-cloud/rio-uikit/TableCardsSorting";
import TableSearch from "@rio-cloud/rio-uikit/TableSearch";
import TableSettingsDialog from "@rio-cloud/rio-uikit/TableSettingsDialog";
import TableToolbar from "@rio-cloud/rio-uikit/TableToolbar";
import TableViewToggles, { type TableViewTogglesViewType, } from "@rio-cloud/rio-uikit/TableViewToggles";
import { formatDateBr } from "@utils";
import Paginator from "../Paginator";
import TableRowLoading from "../TableRowLoading";

const defaultColumnOrder = [
  "nome",
  // "apelido",
  "categoria",
  "endereco",
  "dataCriacao",
  "dataAtualizacao",
  "nomeCriador",
];

type ColumnLabel = {
  [key: string]: string;
};
const columnLabels: ColumnLabel = {
  nome: "Nome",
  // apelido: "Apelido",
  categoria: "Categoria",
  endereco: "Endereço",
  dataCriacao: "Data de criação",
  dataAtualizacao: "Data de atualização",
  nomeCriador: "Criado por",
};

type ColumnDetails = {
  [key: string]: number;
};

type ColumnDetailsMap = {
  [key: string]: ColumnDetails;
};

const demoColumnsDetails: ColumnDetailsMap = {
  name: {
    width: 200,
    defaultWidth: 200,
    maxWidth: 350,
  },
  origin: {
    width: 250,
    defaultWidth: 250,
    maxWidth: 350,
  },
  destination: {
    width: 250,
    defaultWidth: 250,
    maxWidth: 350,
  },
  stops: {
    width: 170,
    defaultWidth: 170,
    maxWidth: 200,
  },
  performance: {
    width: 278,
    defaultWidth: 278,
    maxWidth: 300,
  },
};

type InterestPoint = {
  id: number;
  nome: string;
  categoria: string;
  latitude: number;
  longitude: number;
  endereco: string;
  idCriador: string;
  accountId: string;
  nomeCriador: string;
  dataCriacao: string;
  dataAtualizacao: string;
  idUsuarioUltimaAlteracao: string | null;
  dataDelecao: string | null;
};

const getSortDir = (
  currentDir: SortDirectionType,
  newSortBy: string,
  previousSortBy: string
): SortDirectionType => {
  if (newSortBy === previousSortBy) {

    return currentDir === SortDirection.ASCENDING
      ? SortDirection.DESCENDING
      : SortDirection.ASCENDING;
  }

  return SortDirection.ASCENDING;
};

export type Props = {
  viewType: TableViewTogglesViewType;
  pages: number;
  interestPoints: InterestPoint[];
  searchValue: string;
  page: number;
  pageSize: number;
  handleSearchValueChange: (text: string) => void;
  handleDownload: () => void;
  handleCreateInterestPoint: () => void;
  handleUpdateInterestPoint: (id: number) => void;
  handleDeleteInterestPoint: (id: number) => void;
  handlePage: (params: { page: number, pages?: number }) => void;
  handleSort: (dir: string, newSort: string) => void;
  showHeader?: boolean;
  isLoading: boolean;
};

const TableInterestPoints = (props: Props) => {
  const { viewType: externalViewType } = props;
  const sortBy = useRef("createdAt");
  const sortDir = useRef<SortDirectionType>(SortDirection.DESCENDING);

  const setSortBy = (value: string) => {
    sortBy.current = value;
  };

  const setSortDir = (value: SortDirectionType) => {
    sortDir.current = value;
  };

  const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnOrder);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnsDetails, setColumnsDetails] = useState(demoColumnsDetails);
  const [viewType, setViewType] = useState(
    externalViewType || TableViewToggles.VIEW_TYPE_TABLE
  );

  const handleToggleTableSettingsDialog = () =>
    setShowTableSettingsDialog(!showTableSettingsDialog);
  const handleViewTypeChange = (newViewType: TableViewTogglesViewType) =>
    setViewType(newViewType);

  const handleColumnChange = (
    newColumnOrder: string[],
    newHiddenColumns: string[],
    newColumnsDetails = columnsDetails
  ) => {
    setColumnOrder(newColumnOrder);
    setHiddenColumns(newHiddenColumns);
    setColumnsDetails(newColumnsDetails);
  };

  // For immediate effect
  const handleColumnDetailsChange = (
    column: string,
    newColumnDetails: ColumnDetails
  ) => {
    const updatedColumnsDetails = { ...columnsDetails };
    updatedColumnsDetails[column] = newColumnDetails;
    setColumnsDetails(updatedColumnsDetails);
  };

  const handleSortChange = (event: React.MouseEvent<HTMLElement>) => {
    const newSortBy = event.currentTarget.getAttribute("data-sortby");

    if (!newSortBy) return;

    const previousSortBy = sortBy.current;
    const previousSortDir = sortDir.current;

    const newSortDir = getSortDir(previousSortDir, newSortBy, previousSortBy);

    sortBy.current = newSortBy;
    sortDir.current = newSortDir;

    props.handleSort(newSortDir, newSortBy);
  };

  const handleCardSortChange = (
    newSortBy: string,
    newSortDir: SortDirectionType
  ) => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
    props.handleSort(newSortDir, newSortBy); // dispara na hora
  };

  // May be extracted as a dedicated component but for demo purpose it's shown here
  const renderTableHead = (
    column: string,
    label: string,
    sortByColumn: string,
    sortDirection: SortDirectionType
  ) => {
    const tableHeadClassNames = classNames("user-select-none", "sort-column");

    return (
      <th
        key={column}
        className={tableHeadClassNames}
        onClick={handleSortChange}
        data-field={column}
        data-sortby={column}
        title={label}
      >
        <span>
          {sortByColumn === column ? (
            <SortArrows direction={sortDirection} />
          ) : (
            <SortArrows />
          )}
          <span>{label}</span>
        </span>
      </th>
    );
  };

  const renderTableCaption = (column: string, columnDetails: ColumnDetails) => {
    const style = columnDetails?.width
      ? {
        minWidth: columnDetails.width,
        width: columnDetails.width,
      }
      : {};

    return <col key={column} style={style} />;
  };

  // filter for hidden columns
  const columns = columnOrder.filter((name) => !hiddenColumns.includes(name));

  // filter data to omit hidden columns
  const filteredRows = props.interestPoints;

  // in case a search value is given, filter the data accordingly
  const searchResult = filteredRows;

  // Sort rows according to the sortBy and sortDir settings
  const rows = searchResult;

  const tableClassNames = classNames(
    "table",
    "table-layout-fixed",
    "table-column-overflow-hidden",
    "table-bordered",
    "table-sticky",
    "table-head-filled",
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD &&
    "table-cards table-single-card",
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS &&
    "table-cards table-multi-cards"
  );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;

  const cardSortingSelectOptions = columns.map((column) => {
    return {
      id: column,
      label: columnLabels[column],
      selected: column === sortBy.current,
      disabled: false,
    };
  });

  const icons = {
    id: "",
    nome: "rioglyph rioglyph-layer margin-right-10 text-color-gray text-size-20",
  };

  return (
    <div id="TableCommonDemo">
      {props.showHeader ? (
        <>
          <TableToolbar>
            <div className="table-toolbar-container">
              <div className="table-toolbar-group-left">
                <div className="table-toolbar-column width-100pct">
                  <div className="btn-toolbar table-btn-toolbar">
                    <Button
                      bsStyle={Button.PRIMARY}
                      iconName="rioglyph-plus"
                      onClick={props.handleCreateInterestPoint}
                    >
                      Criar ponto de interesse
                    </Button>
                    {/*<Button>Cadastrar serviços</Button>*/}
                    <TableSearch
                      className="width-400"
                      value={props.searchValue}
                      onChange={props.handleSearchValueChange}
                      placeholder="Buscar por nome, categoria, endereço e criador"
                    />
                  </div>
                </div>
              </div>
              <div className="table-toolbar-group-right">
                <div className="table-toolbar-column">
                  <TableViewToggles
                    initialViewType={viewType}
                    onViewTypeChange={handleViewTypeChange}
                    tableViewTooltipContent="Table view"
                    singleCardViewTooltipContent="List view"
                    multiCardsViewTooltipContent="Cards view"
                  />
                </div>
              </div>
              {showTableSettingsDialog && (
                <TableSettingsDialog
                  show={showTableSettingsDialog}
                  title="Table settings"
                  onHide={handleToggleTableSettingsDialog}
                  onColumnChange={handleColumnChange}
                  defaultColumnOrder={defaultColumnOrder}
                  defaultHiddenColumns={[]}
                  columnOrder={columnOrder}
                  hiddenColumns={hiddenColumns}
                  columnLabels={columnLabels}
                  disabledColumns={[]}
                  closeButtonText="Close"
                  resetButtonText="Reset to default"
                  searchPlaceholder="Search by column name"
                  notFoundMessage="No column found for this search value"
                  columnsDetails={columnsDetails}
                  autoLabel="Auto"
                  onColumnDetailsChange={handleColumnDetailsChange}
                  onSearchChange={(val: string) => console.log(val)}
                  immediateChange
                />
              )}
            </div>
          </TableToolbar>
          {sortBy && !isViewTypeTable && (
            <TableCardsSorting
              selectOptions={cardSortingSelectOptions}
              sortName={sortBy.current}
              sortOrder={sortDir.current}
              onSortChange={handleCardSortChange}
            />
          )}
        </>
      ) : null}

      <div>
        {!props.isLoading && isEmpty(rows) && !isViewTypeTable && (
          <NotFoundState
            headline="Sem resultados"
            message="Por favor refaça a busca."
          />
        )}

        <table className={tableClassNames}>
          <colgroup>
            {columns.map((column) =>
              renderTableCaption(column, columnsDetails[column])
            )}
            <col className="table-action" />
          </colgroup>
          <thead>
            <tr>
              {columns.map((col: string) =>
                renderTableHead(col, columnLabels[col], sortBy.current, sortDir.current)
              )}
              <th className="table-action" />
            </tr>
          </thead>
          <tbody>
              {props.isLoading ? (
                <TableRowLoading cols={7} rows={10} />
              ): null}

            {!props.isLoading && isEmpty(rows) && isViewTypeTable && (
              <tr>
                <td colSpan={columns.length + 1}>
                  <NotFoundState
                    outerClassName="border-none"
                    headline="Sem resultados"
                    message="Por favor refaça a busca."
                  />
                </td>
              </tr>
            )}

            {!props.isLoading && rows.map((row: InterestPoint, index: number) => (
              <tr key={index}>
                {columns.map((col: string) => (
                  <td key={col} data-field={columnLabels[col]}>
                    {col === "nome" && icons[col] && <span className={icons[col]} />}
                    <span>
                      {col === "dataCriacao" || col === "dataAtualizacao"
                        ? formatDateBr(row[col as keyof InterestPoint] as string, false)
                        : String(row[col as keyof InterestPoint] ?? "")}
                    </span>
                  </td>
                ))}
                <td className="table-action">
                  <span>
                    <ButtonDropdown
                      bsSize="md"
                      dropdownClassName="width-200"
                      title={
                        <span className="rioglyph rioglyph-option-vertical" />
                      }
                      variant="link"
                      iconOnly
                      items={[
                        {
                          value: (
                            <div
                              onClick={() => props.handleUpdateInterestPoint(row.id)}
                            >
                              <span className="rioglyph rioglyph-map-location margin-right-10 text-color-primary" />
                              <span>Editar</span>
                            </div>
                          ),
                        },
                        {
                          value: (
                            <div
                              onClick={() => props.handleDeleteInterestPoint(row.id)}
                            >
                              <span className="rioglyph rioglyph-trash margin-right-10 text-color-primary" />
                              <span>Excluir</span>
                            </div>
                          ),
                        },
                      ]}
                    />
                  </span>
                </td>
              </tr>
            ))}
            {/* Placeholder workaround for equal with cards of the last row */}
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
          </tbody>
        </table>
      </div>

      <Paginator
        handlePage={params => props.handlePage(params)}
        page={props.page}
        pages={props.pages}
        pageSize={props.pageSize}
        disabled={rows.length == 0}
      />
    </div>
  );
};

export default TableInterestPoints;
